import { useState, useEffect } from 'react';
import './App.css';
// Removed device and playlist selection flow
// import PlaylistSelector from './components/PlaylistSelector';
import CurrentPlayback from './components/CurrentPlayback';
// import DeviceSelector from './components/DeviceSelector';
import Login from './components/Login';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:5000';

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [playbackState, setPlaybackState] = useState(null);
  // Removed selected device/playlist state
  // const [selectedDevice, setSelectedDevice] = useState(null);
  // const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // New: single source of truth refresh signal for child components
  const [refreshTick, setRefreshTick] = useState(0);

  // Check authentication status on app load
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/auth-status`, {
          credentials: 'include' // Include cookies
        });
        const data = await response.json();
        
        if (data.authenticated) {
          setAuthenticated(true);
          await getUserInfo();
          await checkPlaybackStateInternal();
        } else {
          setAuthenticated(false);
          setLoading(false);
        }
      } catch (err) {
        setError('Failed to check authentication status');
        setLoading(false);
      }
    };

    const getUserInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user-info`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (err) {
        setError('Failed to fetch user info');
      }
    };

    const checkPlaybackStateInternal = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/playback-state`, {
          credentials: 'include'
        });
        
        if (response.status === 401) {
          setAuthenticated(false);
          return;
        }
        
        const data = await response.json();
        setPlaybackState(data);
        setError(null);
      } catch (err) {
        setError('Failed to connect to Spotify. Make sure the backend is running.');
      } finally {
        setLoading(false);
      }
    };

    const checkInitialAuth = async () => {
      // Check for auth success/error in URL first
      const urlParams = new URLSearchParams(window.location.search);
      const authStatus = urlParams.get('auth');
      const errorFromUrl = urlParams.get('error');
      
      if (authStatus === 'success') {
        // Clean up URL and check auth status
        window.history.replaceState({}, document.title, window.location.pathname);
        await checkAuthStatus();
        return;
      }
      
      if (errorFromUrl) {
        setError('Authentication failed. Please try again.');
        window.history.replaceState({}, document.title, window.location.pathname);
        setLoading(false);
        return;
      }
      // Normal auth check
      await checkAuthStatus();
    };
    
    checkInitialAuth();
  }, []);

  const handleLogin = () => {
    // Redirect to login endpoint
    window.location.href = `${API_BASE_URL}/api/login`;
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      setError('Logout failed');
    }
    
    setAuthenticated(false);
    setUser(null);
    setPlaybackState(null);
    setLoading(false);
  };

  const checkPlaybackState = async () => {
    if (!authenticated) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/playback-state`, {
        credentials: 'include'
      });
      
      if (response.status === 401) {
        setAuthenticated(false);
        return;
      }
      
      const data = await response.json();
      setPlaybackState(data);
      setError(null);
    } catch (err) {
      setError('Failed to connect to Spotify. Make sure the backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const refreshPlaybackState = () => {
    // Trigger parent refresh and notify children
    checkPlaybackState();
    setRefreshTick((t) => t + 1);
  };

  if (loading) {
    return (
      <div className="App">
        <div className="container">
          <h1>🎵 True Shuffle</h1>
          <div className="loading">
            <div className="spinner"></div>
            <p>Connecting to Spotify...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!authenticated) {
    return <Login onLogin={handleLogin} />;
  }

  if (error) {
    return (
      <div className="App">
        <div className="container">
          <h1>🎵 True Shuffle</h1>
          <div className="error-state">
            <p>❌ {error}</p>
            <button onClick={refreshPlaybackState} className="retry-btn">
              🔄 Retry Connection
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="container">
        <header className="app-header">
          <div className="header-top">
            <div className="header-left">
              <h1>🎵 True Shuffle</h1>
              <p className="subtitle">Play any song from the playlist you want to shuffle in your Spotify app, then come back here and shuffle it truly at random.</p>
            </div>
            <div className="header-right">
              <div className="user-info">
                {user?.images?.[0] && (
                  <img 
                    src={user.images[0].url} 
                    alt={user.display_name}
                    className="user-avatar"
                  />
                )}
                <span className="user-name">
                  {user?.display_name || user?.id}
                </span>
                <button 
                  onClick={handleLogout}
                  className="logout-btn"
                  title="Logout"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          <div className="refresh-section">
            <button 
              onClick={refreshPlaybackState} 
              className="btn refresh-btn"
              disabled={loading}
            >
              🔄 {loading ? 'Checking...' : 'Refresh'}
            </button>
          </div>

          {/* Front-page prompt */}
          <div className="front-prompt info-banner">
            <h3>Before you shuffle</h3>
            <p>
              Play a song in Spotify from <strong>THE PLAYLIST</strong> you want to shuffle. Then come back here, tap <strong>Refresh</strong>, and press <strong>Shuffle This Playlist</strong>.
            </p>
          </div>
        </header>
        
        {/* Single flow: show current playback and shuffle option */}
        <CurrentPlayback refreshTick={refreshTick} />
      </div>
    </div>
  );
}

export default App;
